import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Users, Plus, Trash2, Upload, User, Pencil, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fileUrl } from "@/components/Certificate";
import { toast } from "sonner";

export default function StaffManagerDialog({ open, onOpenChange }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPos, setNewPos] = useState("");
  const [editing, setEditing] = useState(null); // { id, name, position }
  const [savingEdit, setSavingEdit] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff");
      setStaff(data);
    } catch {
      toast.error("Failed to load staff");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newPos.trim()) return;
    setCreating(true);
    try {
      await api.post("/staff", { name: newName.trim(), position: newPos.trim() });
      setNewName(""); setNewPos("");
      await load();
      toast.success("Staff member added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove ${name} from the team page?`)) return;
    try {
      await api.delete(`/staff/${id}`);
      await load();
      toast.success("Removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to remove");
    }
  };

  const handleUpload = async (id, file) => {
    if (!file) return;
    setUploadingId(id);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/staff/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      toast.success("Photo updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingId(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      await api.patch(`/staff/${editing.id}`, {
        name: editing.name.trim(),
        position: editing.position.trim(),
      });
      setEditing(null);
      await load();
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="staff-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" /> Manage team members
          </DialogTitle>
          <DialogDescription>
            Add, edit, and remove the profiles that appear on the public Our Team page.
          </DialogDescription>
        </DialogHeader>

        {/* Add new */}
        <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end pt-2 border-b border-slate-200 dark:border-slate-800 pb-5">
          <div>
            <Label className="text-sm">Full name</Label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Jane Doe"
              maxLength={120}
              required
              className="h-10 mt-1.5"
              data-testid="staff-new-name-input"
            />
          </div>
          <div>
            <Label className="text-sm">Position</Label>
            <Input
              value={newPos}
              onChange={(e) => setNewPos(e.target.value)}
              placeholder="Chief Executive Officer"
              maxLength={120}
              required
              className="h-10 mt-1.5"
              data-testid="staff-new-position-input"
            />
          </div>
          <Button
            type="submit"
            disabled={creating || !newName.trim() || !newPos.trim()}
            className="h-10 bg-blue-600 hover:bg-blue-700"
            data-testid="staff-add-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> {creating ? "Adding…" : "Add"}
          </Button>
        </form>

        {/* Existing list */}
        <div className="space-y-3 mt-2">
          {loading && <div className="text-sm text-slate-500 text-center py-6">Loading…</div>}
          {!loading && staff.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-6">No team members yet — add the first one above.</div>
          )}
          {!loading && staff.map((s) => (
            <Card key={s.id} className="p-4 border-slate-200 dark:border-slate-800" data-testid={`staff-row-${s.id}`}>
              <div className="flex items-center gap-4 flex-wrap">
                {/* Photo */}
                <div className="w-16 h-16 rounded-md overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                  {s.photo_path ? (
                    <img src={fileUrl(s.photo_path)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-6 h-6 text-slate-400" />
                  )}
                </div>

                {/* Details / edit form */}
                <div className="flex-1 min-w-0">
                  {editing?.id === s.id ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Input
                        value={editing.name}
                        onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
                        placeholder="Full name"
                        className="h-9"
                        data-testid={`staff-edit-name-${s.id}`}
                      />
                      <Input
                        value={editing.position}
                        onChange={(e) => setEditing((s) => ({ ...s, position: e.target.value }))}
                        placeholder="Position"
                        className="h-9"
                        data-testid={`staff-edit-position-${s.id}`}
                      />
                    </div>
                  ) : (
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 dark:text-white truncate" data-testid={`staff-name-${s.id}`}>{s.name}</div>
                      <div className="text-sm text-slate-500 dark:text-slate-400 truncate" data-testid={`staff-position-${s.id}`}>{s.position}</div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {editing?.id === s.id ? (
                    <>
                      <Button
                        size="sm"
                        onClick={handleSaveEdit}
                        disabled={savingEdit || !editing.name.trim() || !editing.position.trim()}
                        className="h-9 bg-blue-600 hover:bg-blue-700"
                        data-testid={`staff-save-${s.id}`}
                      >
                        {savingEdit ? "Saving…" : "Save"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="h-9" data-testid={`staff-cancel-${s.id}`}>
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <label className={`inline-flex items-center gap-1.5 h-9 px-3 text-xs rounded-md border cursor-pointer ${uploadingId === s.id ? "border-slate-200 text-slate-400 cursor-wait" : "border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          className="hidden"
                          disabled={uploadingId === s.id}
                          onChange={(e) => handleUpload(s.id, e.target.files?.[0])}
                          data-testid={`staff-upload-${s.id}`}
                        />
                        <Upload className="w-3 h-3" />
                        {uploadingId === s.id ? "Uploading…" : (s.photo_path ? "Replace" : "Photo")}
                      </label>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setEditing({ id: s.id, name: s.name, position: s.position })}
                        className="h-9 w-9"
                        title="Edit"
                        data-testid={`staff-edit-${s.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(s.id, s.name)}
                        className="h-9 w-9 text-slate-400 hover:text-red-600"
                        title="Remove"
                        data-testid={`staff-delete-${s.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="staff-close-btn">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
